import {
  Box,
  Flex,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from "@chakra-ui/react";
import { Search } from "lucide-react";
import { useMemo, useState } from "react";
import type { MFReviewAccount } from "state/members/accessReviews";
import { Avatar } from "components/ds";
import { reviewAccountDisplayName } from "../reviewLogic";

interface AccountChooserModalProps {
  isOpen: boolean;
  onClose: () => void;
  accounts: MFReviewAccount[];
  onSelect: (accountId: string) => void;
}

/**
 * AccountChooserModal — picks a single account when "Fix incorrect data" is
 * launched from the header menu (no row context). Selecting an account hands its
 * id back so the caller can open the Fix-incorrect-data modal for it.
 */
const AccountChooserModal = ({
  isOpen,
  onClose,
  accounts,
  onSelect,
}: AccountChooserModalProps) => {
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return accounts;
    return accounts.filter((a) => {
      const label = reviewAccountDisplayName(a).toLowerCase();
      const email = a.member?.email?.toLowerCase() ?? "";
      return label.includes(q) || email.includes(q);
    });
  }, [accounts, query]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
      <ModalOverlay bg="rgba(0, 0, 0, 0.2)" backdropFilter="blur(2px)" />
      <ModalContent borderRadius="12px" boxShadow="lg" overflow="hidden">
        <ModalHeader
          borderBottom="1px solid"
          borderColor="border.default"
          py={4}
          px={5}
          fontSize="15px"
          fontWeight="600"
          color="text.body.primary"
        >
          Choose an account to fix
        </ModalHeader>
        <ModalCloseButton top={3} right={3} />
        <ModalBody py={4} px={5}>
          <VStack spacing={3} align="stretch">
            <InputGroup size="sm">
              <InputLeftElement pointerEvents="none">
                <Icon as={Search} boxSize="14px" color="text.body.tertiary" />
              </InputLeftElement>
              <Input
                placeholder="Search accounts…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                borderRadius="6px"
                fontSize="13px"
                h="34px"
                autoFocus
              />
            </InputGroup>

            <Box
              maxHeight="360px"
              overflowY="auto"
              sx={{
                "&::-webkit-scrollbar": { width: "4px" },
                "&::-webkit-scrollbar-thumb": {
                  bg: "border.default",
                  borderRadius: "4px",
                },
              }}
            >
              <VStack align="stretch" spacing={1}>
                {filtered.length === 0 ? (
                  <Text
                    fontSize="13px"
                    color="text.body.tertiary"
                    textAlign="center"
                    py={6}
                  >
                    No matching accounts
                  </Text>
                ) : (
                  filtered.map((account) => (
                    <Flex
                      key={account.id}
                      align="center"
                      gap={3}
                      px={3}
                      py="8px"
                      borderRadius="8px"
                      cursor="pointer"
                      _hover={{ bg: "bg.section" }}
                      onClick={() => onSelect(account.id)}
                    >
                      <Avatar
                        name={reviewAccountDisplayName(account)}
                        src={account.member?.profilePictureUrl ?? undefined}
                        size="sm"
                      />
                      <VStack align="flex-start" spacing={0} minW={0}>
                        <Text
                          fontSize="13px"
                          fontWeight="500"
                          color="text.body.primary"
                          noOfLines={1}
                        >
                          {reviewAccountDisplayName(account)}
                        </Text>
                        {account.member?.email && (
                          <Text
                            fontSize="12px"
                            color="text.body.tertiary"
                            noOfLines={1}
                          >
                            {account.member.email}
                          </Text>
                        )}
                      </VStack>
                    </Flex>
                  ))
                )}
              </VStack>
            </Box>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default AccountChooserModal;
